/**
 * Hook pour charger le contenu dynamique d'une page depuis le CMS
 * Y GO Shuttle Services France
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

/**
 * Hook pour récupérer le contenu d'une page CMS
 * @param {string} pageSlug - Slug de la page (ex: 'home', 'flotte', 'prestations')
 * @returns {Object} { content, loading, error }
 */
export const useCMSPage = (pageSlug) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      if (!pageSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/cms/pages/${pageSlug}/public`);
        setContent(response.data);
        setError(null);
      } catch (err) {
        console.error(`Failed to fetch CMS content for page: ${pageSlug}`, err);
        setError(err);
        setContent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [pageSlug]);

  return { content, loading, error };
};

/**
 * Hook pour récupérer le branding du site
 * @returns {Object} { branding, loading, error }
 */
export const useSiteBranding = () => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/cms/branding/public`);
        setBranding(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch site branding', err);
        setError(err);
        setBranding(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  return { branding, loading, error };
};

/**
 * Helper pour obtenir une valeur CMS avec fallback
 * @param {Object} cmsContent - Contenu CMS
 * @param {string} path - Chemin vers la valeur (ex: 'banner.title')
 * @param {any} fallback - Valeur par défaut si non trouvée
 * @returns {any}
 */
export const getCMSValue = (cmsContent, path, fallback = null) => {
  if (!cmsContent) return fallback;
  
  const keys = path.split('.');
  let value = cmsContent;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return fallback;
    }
  }
  
  return value || fallback;
};

/**
 * Helper pour obtenir une section CMS par type ou ID
 * @param {Object} cmsContent - Contenu CMS
 * @param {string} sectionType - Type de section (ex: 'hero', 'services')
 * @returns {Object|null}
 */
export const getCMSSection = (cmsContent, sectionType) => {
  if (!cmsContent?.sections) return null;
  
  return cmsContent.sections.find(
    s => s.type === sectionType && s.is_active !== false
  ) || null;
};

export default useCMSPage;
